import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, StatusBadge, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { alarmsAPI, sitesAPI } from '../utils/api';
import { formatDateTime } from '../utils/helpers';
import { Bell, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

export const AlarmsPage = () => {
  const [alarms, setAlarms] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unresolved');

  // Resolve modal state
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const [alarmsRes, sitesRes] = await Promise.all([
        alarmsAPI.getAll(null, filter === 'resolved' ? true : false),
        sitesAPI.getAll(),
      ]);
      setAlarms(alarmsRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      toast.error('Failed to load alarms');
    } finally {
      setLoading(false);
    }
  };

  const getSiteName = (siteId) => {
    return sites.find((s) => s.site_id === siteId)?.name || 'Unknown';
  };

  const handleAcknowledge = async (alarmId) => {
    try {
      await alarmsAPI.acknowledge(alarmId);
      toast.success('Alarm acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge alarm');
    }
  };

  const openResolveModal = (alarm) => {
    setResolveTarget(alarm);
    setResolveNotes('');
    setRootCause('');
    setCorrectiveAction('');
    setShowResolveModal(true);
  };

  const handleResolve = async () => {
    if (!resolveNotes.trim()) {
      toast.error('Resolution notes are required');
      return;
    }
    setResolving(true);
    try {
      await alarmsAPI.resolve(resolveTarget.alarm_id, {
        resolution_notes: resolveNotes,
        root_cause_category: rootCause,
        corrective_action: correctiveAction,
      });
      toast.success('Alarm resolved');
      setShowResolveModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve alarm');
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <PageHeader
            title="Alarms & Events"
            description="Monitor and manage system alarms"
          />

          {/* Filter Tabs */}
          <div className="mb-6 flex space-x-2">
            <Button
              variant={filter === 'unresolved' ? 'default' : 'outline'}
              onClick={() => setFilter('unresolved')}
              data-testid="filter-unresolved"
              className={filter === 'unresolved' ? 'bg-slate-900' : ''}
            >
              Active Alarms
            </Button>
            <Button
              variant={filter === 'resolved' ? 'default' : 'outline'}
              onClick={() => setFilter('resolved')}
              data-testid="filter-resolved"
              className={filter === 'resolved' ? 'bg-slate-900' : ''}
            >
              Resolved
            </Button>
          </div>

          {/* Alarms List */}
          {alarms.length === 0 ? (
            <EmptyState
              icon={Bell}
              title={filter === 'unresolved' ? 'No active alarms' : 'No resolved alarms'}
              description={filter === 'unresolved' ? 'All systems operating normally' : 'No alarms have been resolved yet'}
            />
          ) : (
            <div className="space-y-4">
              {alarms.map((alarm) => (
                <div
                  key={alarm.alarm_id}
                  data-testid={`alarm-${alarm.alarm_id}`}
                  className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                        alarm.severity === 'critical' ? 'bg-red-50' :
                        alarm.severity === 'warning' ? 'bg-amber-50' : 'bg-blue-50'
                      }`}>
                        <AlertTriangle className={`w-6 h-6 ${
                          alarm.severity === 'critical' ? 'text-red-600' :
                          alarm.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-slate-900 text-lg">{alarm.title}</h3>
                          <StatusBadge status={alarm.severity} />
                          {alarm.acknowledged && (
                            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                              Acknowledged
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 mb-3">{alarm.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-slate-500">
                          <span>{getSiteName(alarm.site_id)}</span>
                          <span>·</span>
                          <span>{formatDateTime(alarm.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                    {!alarm.resolved && (
                      <div className="flex items-center space-x-2 ml-4">
                        {!alarm.acknowledged && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledge(alarm.alarm_id)}
                            data-testid={`acknowledge-alarm-${alarm.alarm_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Acknowledge
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => openResolveModal(alarm)}
                          data-testid={`resolve-alarm-${alarm.alarm_id}`}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Resolve
                        </Button>
                      </div>
                    )}
                  </div>

                  {alarm.resolved && (
                    <div className="pt-4 border-t border-slate-100 text-sm">
                      <div className="flex items-center gap-4">
                        <span className="text-green-600 font-medium">Resolved</span>
                        <span className="text-slate-500">{formatDateTime(alarm.resolved_at)}</span>
                      </div>
                      {alarm.resolution_notes && (
                        <div className="mt-2 bg-slate-50 rounded p-3 text-xs">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Resolution Notes</p>
                          <p className="text-slate-700">{alarm.resolution_notes}</p>
                          {alarm.root_cause_category && (
                            <p className="text-slate-500 mt-1">Root Cause: <span className="font-medium">{alarm.root_cause_category}</span></p>
                          )}
                          {alarm.corrective_action && (
                            <p className="text-slate-500 mt-1">Corrective Action: <span className="font-medium">{alarm.corrective_action}</span></p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Resolve Modal */}
      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent className="max-w-md" data-testid="resolve-alarm-modal">
          <DialogHeader>
            <DialogTitle>Resolve Alarm</DialogTitle>
          </DialogHeader>
          {resolveTarget && (
            <div className="bg-slate-50 rounded p-3 mb-2 text-xs">
              <p className="font-medium text-slate-700">{resolveTarget.title}</p>
              <p className="text-slate-500 mt-0.5">{resolveTarget.description}</p>
            </div>
          )}
          <div className="space-y-4 py-1">
            <div>
              <Label>Resolution Notes *</Label>
              <Textarea
                value={resolveNotes}
                onChange={e => setResolveNotes(e.target.value)}
                placeholder="Describe what was done to resolve this alarm..."
                className="mt-1"
                data-testid="resolve-notes-input"
              />
            </div>
            <div>
              <Label>Root Cause Category <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                value={rootCause}
                onChange={e => setRootCause(e.target.value)}
                placeholder="e.g., Hardware failure, Configuration error, Software bug..."
                className="mt-1"
                data-testid="resolve-root-cause-input"
              />
            </div>
            <div>
              <Label>Corrective Action <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Textarea
                value={correctiveAction}
                onChange={e => setCorrectiveAction(e.target.value)}
                placeholder="Steps taken to prevent recurrence..."
                className="mt-1"
                rows={2}
                data-testid="resolve-corrective-action-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveModal(false)}>Cancel</Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={handleResolve}
              disabled={resolving}
              data-testid="resolve-confirm-btn"
            >
              {resolving ? 'Resolving...' : 'Resolve Alarm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
